import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AnnotationCanvas from '../components/annotation/AnnotationCanvas'
import KeypointSelector from '../components/annotation/KeypointSelector'
import MessageNotification from '../components/messaging/notifications/MessageNotification'
import StatusBadge from '../components/projects/StatusBadge'
import { useNavigationGuard } from '../hooks/useNavigationGuard'
import { projectAPI, imageAPI, annotationAPI, userProjectAPI } from '../services/api'
import {
  Project,
  ImageData,
  Keypoint,
  ImageAnnotation,
  UserProject,
  COCOAnnotation,
  ProjectStatus,
} from '../types'
import {
  initializeEmptyKeypoints,
  keypointsToCOCOFormat,
  cocoFormatToKeypoints,
} from '../utils/cocoFormat'
import {
  findNextUnsetKeypointInDisplayOrder,
  navigateKeypointInDisplayOrder,
  getKeypointDisplayOrder,
} from '../utils/keypointOrder'
import { normalizeKeypointsLength, isKeypointUnset } from '../utils/keypointUtils'

export default function ProjectAnnotationPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()

  const [project, setProject] = useState<Project | null>(null)
  const [images, setImages] = useState<ImageData[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [currentKeypoint, setCurrentKeypoint] = useState(0)
  const [annotations, setAnnotations] = useState<COCOAnnotation[]>([])
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [userProjectStatus, setUserProjectStatus] = useState<UserProject | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [imageAnnotations, setImageAnnotations] = useState<Map<string, ImageAnnotation>>(new Map())
  const [keypointRadius, setKeypointRadius] = useState(5)
  const [rectangleWidth, setRectangleWidth] = useState(3)
  const [isPoorQuality, setIsPoorQuality] = useState(false)
  const [currentViewSettings, setCurrentViewSettings] = useState<{
    scale?: number
    offset?: { x: number; y: number }
    keypointRadius?: number
    rectangleWidth?: number
  } | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(() => {
    // Load fullscreen state from localStorage
    const savedState = localStorage.getItem('annotation-fullscreen')
    return savedState === 'true'
  })

  // Track if navigation was triggered by keyboard or auto-advance
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false)
  // Track if canvas should auto-fit to bounding box
  const [shouldAutoFit, setShouldAutoFit] = useState(false)

  // Refs for scrollable containers
  const keypointScrollRef = useRef<HTMLDivElement>(null)
  const frameScrollRef = useRef<HTMLDivElement>(null)

  // Refs for current values to avoid re-running effects
  const keypointRadiusRef = useRef(keypointRadius)
  const rectangleWidthRef = useRef(rectangleWidth)

  // Update refs when values change
  useEffect(() => {
    keypointRadiusRef.current = keypointRadius
  }, [keypointRadius])

  useEffect(() => {
    rectangleWidthRef.current = rectangleWidth
  }, [rectangleWidth])

  // Use navigation guard for global navigation protection
  useNavigationGuard({
    hasUnsavedChanges,
    onConfirmSave: async () => {
      await saveAnnotations()
    },
  })

  // === Effect Hooks ===

  // Initial data loading
  useEffect(() => {
    if (projectId) {
      loadProjectData()
    }
  }, [projectId])

  // Persist settings to localStorage
  useEffect(() => {
    localStorage.setItem('annotation-fullscreen', isFullscreen.toString())
  }, [isFullscreen])

  // Browser navigation warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  // Auto-scroll functionality
  useEffect(() => {
    if (!shouldAutoScroll) return

    const scrollToElement = (
      _containerId: string,
      elementId: string,
      ref: React.RefObject<HTMLDivElement>
    ) => {
      if (!ref.current) return

      setTimeout(() => {
        const element = document.getElementById(elementId)
        if (element && ref.current) {
          const containerRect = ref.current.getBoundingClientRect()
          const elementRect = element.getBoundingClientRect()
          const relativeTop = elementRect.top - containerRect.top + ref.current.scrollTop
          ref.current.scrollTop = relativeTop
        }
      }, 50)
    }

    // Scroll to current keypoint
    if (selectedAnnotationId) {
      scrollToElement('keypoint-list', `keypoint-${currentKeypoint}`, keypointScrollRef)
    }

    // Scroll to current frame
    scrollToElement('frame-list', `frame-${currentImageIndex}`, frameScrollRef)

    // Reset auto-scroll flag after scrolling
    setShouldAutoScroll(false)
  }, [currentKeypoint, selectedAnnotationId, currentImageIndex, shouldAutoScroll])

  // Load keypointRadius and rectangleWidth from currentViewSettings when loading an image with saved settings
  useEffect(() => {
    if (currentViewSettings) {
      // Only update if the values are explicitly defined in view settings
      if (currentViewSettings.keypointRadius !== undefined) {
        setKeypointRadius(currentViewSettings.keypointRadius)
      }
      if (currentViewSettings.rectangleWidth !== undefined) {
        setRectangleWidth(currentViewSettings.rectangleWidth)
      }
    }
    // Don't reset to defaults when currentViewSettings is null
    // Keep the current values that user has set
  }, [currentViewSettings])

  const loadProjectData = async () => {
    try {
      setLoading(true)
      const [projectRes, imagesRes, statusRes] = await Promise.all([
        projectAPI.get(projectId!),
        imageAPI.listAll(projectId!),
        userProjectAPI.getProjectStatus(projectId!),
      ])
      setProject(projectRes.data)
      setImages(imagesRes.data)
      setUserProjectStatus(statusRes.data)

      // Load all user's annotations for this project in one request
      const annotationsMap = new Map<string, ImageAnnotation>()
      try {
        const annotationsRes = await annotationAPI.getMyProjectAnnotations(projectId!)
        for (const annotation of annotationsRes.data) {
          annotationsMap.set(annotation.image_id, annotation)
        }
      } catch (err) {
        console.error('Error loading annotations:', err)
      }
      setImageAnnotations(annotationsMap)

      // Initialize first image - will be handled by initializeAnnotations via useEffect
    } catch (err) {
      console.error('Error loading project:', err)
      alert('Failed to load project')
    } finally {
      setLoading(false)
    }
  }

  // Helper function to create default view settings
  const createDefaultViewSettings = (
    overrides?: Partial<typeof currentViewSettings>,
    currentKeypointRadius?: number,
    currentRectangleWidth?: number
  ) => ({
    scale: overrides?.scale,
    offset: overrides?.offset,
    keypointRadius: overrides?.keypointRadius ?? currentKeypointRadius ?? 5,
    rectangleWidth: overrides?.rectangleWidth ?? currentRectangleWidth ?? 3,
  })

  // Helper function to load view settings from annotation
  const loadViewSettingsFromAnnotation = (annotation: ImageAnnotation | undefined) => {
    if (annotation?.view_settings?.scale && annotation?.view_settings?.offset) {
      // Image has saved scale/offset settings, use them
      setCurrentViewSettings({
        scale: annotation.view_settings.scale,
        offset: annotation.view_settings.offset,
        keypointRadius: annotation.view_settings.keypointRadius ?? 5,
        rectangleWidth: annotation.view_settings.rectangleWidth ?? 3,
      })
      setShouldAutoFit(false) // Don't auto-fit when we have saved settings
    } else {
      // No saved scale/offset - trigger auto-fit to screen
      setCurrentViewSettings(prev => createDefaultViewSettings(prev))
      setShouldAutoFit(true) // Trigger auto-fit to screen for images without scale/offset
    }
  }

  // Helper to safely get keypoint schema
  const getKeypointSchema = () => {
    if (!project?.keypoint_schema) {
      console.warn('Project or keypoint_schema is undefined')
      return []
    }
    return project.keypoint_schema
  }

  // Helper to safely get keypoint schema length
  const getKeypointSchemaLength = () => {
    const schema = getKeypointSchema()
    return schema.length
  }

  // Helper function to auto-select annotation and keypoint
  const autoSelectAnnotationAndKeypoint = (annotations: COCOAnnotation[]) => {
    if (annotations.length > 0) {
      const lastAnnotation = annotations[annotations.length - 1]
      setSelectedAnnotationId(lastAnnotation.id)

      // Set current keypoint to first unset in display order
      const rawKeypoints = cocoFormatToKeypoints(lastAnnotation.keypoints)
      const schemaLength = getKeypointSchemaLength()
      const schema = getKeypointSchema()

      if (schemaLength === 0) {
        setCurrentKeypoint(0)
        return
      }

      const keypoints = normalizeKeypointsLength(rawKeypoints, schemaLength)
      const nextUnset = findNextUnsetKeypointInDisplayOrder(keypoints, -1, schema)
      if (nextUnset !== -1) {
        setCurrentKeypoint(nextUnset)
      } else {
        // If all keypoints are set, start from first in display order
        const displayOrder = getKeypointDisplayOrder(schema)
        setCurrentKeypoint(displayOrder[0] || 0)
      }
    } else {
      setSelectedAnnotationId(null)
    }
  }

  // Preload adjacent images for faster navigation
  const preloadImages = (index: number) => {
    const preloadIndexes = [index - 1, index + 1].filter(i => i >= 0 && i < images.length)
    preloadIndexes.forEach(i => {
      const img = new Image()
      // IMPORTANT: Must set crossOrigin to match how AnnotationCanvas loads images
      // Otherwise, cached images will cause CORS errors
      img.crossOrigin = 'anonymous'
      img.src = images[i].url || ''
    })
  }

  const initializeAnnotations = useCallback(async () => {
    if (!project || !images[currentImageIndex]) return

    const currentImage = images[currentImageIndex]
    const existingAnnotation = imageAnnotations.get(currentImage.id)

    // Preload adjacent images
    preloadImages(currentImageIndex)

    if (existingAnnotation?.annotations) {
      // Load existing annotations
      setAnnotations(existingAnnotation.annotations)
      setIsPoorQuality(existingAnnotation.meta_data?.poor_quality || false)

      // Load view settings
      loadViewSettingsFromAnnotation(existingAnnotation)

      // Auto-select annotation and keypoint
      autoSelectAnnotationAndKeypoint(existingAnnotation.annotations)
    } else {
      // Reset to empty state
      setAnnotations([])
      setIsPoorQuality(false)
      setSelectedAnnotationId(null)

      // No annotations, but still trigger auto-fit to screen for new images
      setCurrentViewSettings(prev =>
        createDefaultViewSettings(prev, keypointRadiusRef.current, rectangleWidthRef.current)
      )
      setShouldAutoFit(true) // Auto-fit to screen even without annotations
    }

    setHasUnsavedChanges(false)
  }, [project, images, currentImageIndex, imageAnnotations])

  // Initialize annotations when project/image changes
  useEffect(() => {
    if (project && images[currentImageIndex]) {
      initializeAnnotations()
    }
  }, [project, currentImageIndex, initializeAnnotations])

  // Auto-scroll only on initial project load
  useEffect(() => {
    if (project && images.length > 0) {
      setShouldAutoScroll(true)
    }
  }, [project?.id]) // Only trigger on project change, not image changes

  const handleAnnotationSelect = (annotationId: string | null) => {
    // Only reset currentKeypoint when the selection actually changes
    const selectionChanged = selectedAnnotationId !== annotationId
    setSelectedAnnotationId(annotationId)
    if (annotationId && selectionChanged) {
      const annotation = annotations.find(a => a.id === annotationId)
      if (annotation) {
        const rawKeypoints = cocoFormatToKeypoints(annotation.keypoints)
        const schemaLength = getKeypointSchemaLength()
        const schema = getKeypointSchema()

        if (schemaLength === 0) {
          setCurrentKeypoint(0)
          return
        }

        const keypoints = normalizeKeypointsLength(rawKeypoints, schemaLength)
        const isCurrentUnset =
          currentKeypoint < keypoints.length && isKeypointUnset(keypoints[currentKeypoint])

        if (isCurrentUnset) {
          // Preserve user's currently chosen keypoint if it's unset in the new selection
          setCurrentKeypoint(currentKeypoint)
        } else {
          // Otherwise jump to the first unset in display order
          const nextUnset = findNextUnsetKeypointInDisplayOrder(keypoints, -1, schema)
          setCurrentKeypoint(nextUnset !== -1 ? nextUnset : 0)
        }
      }
    }
  }

  const handleAnnotationAdd = (annotation: COCOAnnotation) => {
    if (!checkEditPermission()) return

    setAnnotations([...annotations, annotation])
    setSelectedAnnotationId(annotation.id)
    setHasUnsavedChanges(true)
    // Start from first keypoint in display order
    const schema = getKeypointSchema()
    const displayOrder = getKeypointDisplayOrder(schema)
    setCurrentKeypoint(displayOrder[0] || 0)
  }

  const handleAnnotationUpdate = (annotationId: string, updatedAnnotation: COCOAnnotation) => {
    if (!checkEditPermission()) return

    setAnnotations(annotations.map(a => (a.id === annotationId ? updatedAnnotation : a)))
    setHasUnsavedChanges(true)
  }

  const handleAnnotationDelete = (annotationId: string) => {
    if (!checkEditPermission()) return

    setAnnotations(annotations.filter(a => a.id !== annotationId))
    if (selectedAnnotationId === annotationId) {
      setSelectedAnnotationId(null)
    }
    setHasUnsavedChanges(true)
  }

  const handleKeypointUpdate = (
    annotationId: string,
    keypointIndex: number,
    keypoint: Keypoint
  ) => {
    if (!checkEditPermission()) return

    const annotation = annotations.find(a => a.id === annotationId)
    if (!annotation) return

    // Normalize keypoints length to match schema to avoid out-of-bounds issues
    const rawKeypoints = cocoFormatToKeypoints(annotation.keypoints)
    const schemaLength = getKeypointSchemaLength()
    const keypoints = normalizeKeypointsLength(rawKeypoints, schemaLength)
    keypoints[keypointIndex] = keypoint

    const updatedAnnotation = {
      ...annotation,
      keypoints: keypointsToCOCOFormat(keypoints),
      num_keypoints: keypoints.filter(kp => kp.x >= 0 && kp.y >= 0).length,
    }

    // Use internal update to avoid double permission check
    setAnnotations(annotations.map(a => (a.id === annotationId ? updatedAnnotation : a)))
    setHasUnsavedChanges(true)

    // Auto-advance to next unset keypoint in display order
    const schema = getKeypointSchema()
    const nextUnset = findNextUnsetKeypointInDisplayOrder(keypoints, keypointIndex, schema)
    if (nextUnset !== -1) {
      setCurrentKeypoint(nextUnset)
      setShouldAutoScroll(true) // Enable auto-scroll for auto-advance
    }
  }

  const handleClearKeypoint = (index: number) => {
    if (!selectedAnnotationId) return
    if (!checkEditPermission()) return

    // Use internal keypoint update to avoid double permission check
    const annotation = annotations.find(a => a.id === selectedAnnotationId)
    if (!annotation) return

    const rawKeypoints = cocoFormatToKeypoints(annotation.keypoints)
    const schemaLength = getKeypointSchemaLength()
    const keypoints = normalizeKeypointsLength(rawKeypoints, schemaLength)
    keypoints[index] = {
      x: -1,
      y: -1,
      confidence: 1.0,
      visibility: 'not_labeled',
    }

    const updatedAnnotation = {
      ...annotation,
      keypoints: keypointsToCOCOFormat(keypoints),
      num_keypoints: keypoints.filter(kp => kp.x >= 0 && kp.y >= 0).length,
    }

    setAnnotations(annotations.map(a => (a.id === selectedAnnotationId ? updatedAnnotation : a)))
    setHasUnsavedChanges(true)
  }

  const handleClearAll = () => {
    if (!selectedAnnotationId || !project) return
    if (!checkEditPermission()) return

    if (confirm('Clear all keypoints for this annotation?')) {
      const annotation = annotations.find(a => a.id === selectedAnnotationId)
      if (!annotation) return

      const emptyKeypoints = initializeEmptyKeypoints(project.keypoint_schema.length)
      const updatedAnnotation = {
        ...annotation,
        keypoints: keypointsToCOCOFormat(emptyKeypoints),
        num_keypoints: 0,
      }

      // Use internal update to avoid double permission check
      setAnnotations(annotations.map(a => (a.id === selectedAnnotationId ? updatedAnnotation : a)))
      setHasUnsavedChanges(true)
    }
  }

  const saveAnnotations = useCallback(async () => {
    if (!project || !images[currentImageIndex]) return

    try {
      setSaving(true)
      const currentImage = images[currentImageIndex]

      // Always save the complete current view settings
      const completeViewSettings = {
        scale: currentViewSettings?.scale,
        offset: currentViewSettings?.offset,
        keypointRadius: keypointRadius,
        rectangleWidth: rectangleWidth,
      }

      const requestData = {
        annotations: annotations,
        meta_data: {
          poor_quality: isPoorQuality,
        },
        view_settings: completeViewSettings,
      }

      const response = await annotationAPI.saveAnnotation(projectId!, currentImage.id, requestData)

      // Update the imageAnnotations map without triggering re-initialization
      const newAnnotationsMap = new Map(imageAnnotations)
      newAnnotationsMap.set(currentImage.id, response.data)
      setImageAnnotations(newAnnotationsMap)

      // Refresh status (backend automatically transitions to DRAFT when needed)
      const statusRes = await userProjectAPI.getProjectStatus(projectId!)
      setUserProjectStatus(statusRes.data)

      setHasUnsavedChanges(false)
    } catch (err: any) {
      console.error('Error saving annotations:', err)
      console.error('Error details:', err.response?.data)
      const errorMessage = err.response?.data?.detail || 'Failed to save annotations'
      alert(errorMessage)
    } finally {
      setSaving(false)
    }
  }, [
    project,
    images,
    currentImageIndex,
    annotations,
    isPoorQuality,
    imageAnnotations,
    currentViewSettings,
    keypointRadius,
    rectangleWidth,
  ])

  // Check if editing should be allowed (warn if QA approved)
  const checkEditPermission = (): boolean => {
    if (userProjectStatus?.status === ProjectStatus.QA_APPROVED) {
      return confirm(
        'This project has been QA Approved. Making changes will set the status back to Draft and you will need to submit for QA again.\n\nDo you want to continue?'
      )
    }
    return true
  }

  const navigateImage = async (newIndex: number, isKeyboardNavigation = false) => {
    if (hasUnsavedChanges) {
      const shouldSave = confirm(
        'You have unsaved changes. Do you want to save them before switching images?'
      )
      if (shouldSave) {
        await saveAnnotations()
      }
    }

    setCurrentImageIndex(newIndex)
    // Start from first keypoint in display order
    const schema = getKeypointSchema()
    const displayOrder = getKeypointDisplayOrder(schema)
    setCurrentKeypoint(displayOrder[0] || 0)

    // Set auto-scroll flag if triggered by keyboard
    if (isKeyboardNavigation) {
      setShouldAutoScroll(true)
    }
  }

  const handleSubmitForQA = async () => {
    try {
      // Check if user has unsaved changes
      if (hasUnsavedChanges) {
        const shouldSave = confirm(
          'You have unsaved changes. Do you want to save them before submitting for QA?'
        )
        if (shouldSave) {
          await saveAnnotations()
        }
      }

      // Use backend complete endpoint to handle status transition
      await userProjectAPI.markProjectComplete(projectId!)

      // Refresh status
      const statusRes = await userProjectAPI.getProjectStatus(projectId!)
      setUserProjectStatus(statusRes.data)
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to submit project for QA'
      alert(errorMessage)
    }
  }

  const handleWithdrawFromQA = async () => {
    try {
      const confirmWithdraw = confirm(
        'Are you sure you want to withdraw this project from QA review? It will be set back to Draft status.'
      )
      if (!confirmWithdraw) return

      // Use backend incomplete endpoint to withdraw from QA
      await userProjectAPI.markProjectIncomplete(projectId!)

      // Refresh status
      const statusRes = await userProjectAPI.getProjectStatus(projectId!)
      setUserProjectStatus(statusRes.data)
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to withdraw project from QA'
      alert(errorMessage)
    }
  }

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev)
  }, [])

  const handleViewSettingsChange = useCallback(
    (settings: {
      scale?: number
      offset?: { x: number; y: number }
      showGrid?: boolean
      autoAIEnabled?: boolean
      keypointRadius?: number
      rectangleWidth?: number
      showOnlySelected?: boolean
      currentTool?: 'select' | 'pan'
    }) => {
      // Extract settings for localStorage (tool settings + showGrid)
      const { autoAIEnabled, showOnlySelected, currentTool, showGrid, ...imageSettings } = settings

      // Save tool settings and showGrid to localStorage
      if (autoAIEnabled !== undefined) {
        localStorage.setItem('annotation-autoAIEnabled', autoAIEnabled.toString())
      }
      if (showOnlySelected !== undefined) {
        localStorage.setItem('annotation-showOnlySelected', showOnlySelected.toString())
      }
      if (currentTool !== undefined) {
        localStorage.setItem('annotation-currentTool', currentTool)
      }
      if (showGrid !== undefined) {
        localStorage.setItem('annotation-showGrid', showGrid.toString())
      }

      // Update local state for keypoint radius and rectangle width
      if (settings.keypointRadius !== undefined && settings.keypointRadius !== keypointRadius) {
        setKeypointRadius(settings.keypointRadius)
      }
      if (settings.rectangleWidth !== undefined && settings.rectangleWidth !== rectangleWidth) {
        setRectangleWidth(settings.rectangleWidth)
      }

      // Update view settings state (will be saved with annotations)
      // Include keypointRadius and rectangleWidth in view settings
      setCurrentViewSettings(prev => ({
        ...prev,
        scale: imageSettings.scale,
        offset: imageSettings.offset,
        keypointRadius: settings.keypointRadius ?? keypointRadius,
        rectangleWidth: settings.rectangleWidth ?? rectangleWidth,
      }))
      // Don't trigger unsaved changes for view settings
    },
    [keypointRadius, rectangleWidth]
  )

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Check if the active element is an input or textarea
      const activeElement = document.activeElement
      const isInputActive =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.getAttribute('contenteditable') === 'true'

      // If typing in an input field, only allow Ctrl/Cmd+S and F11
      if (isInputActive) {
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
          e.preventDefault()
          saveAnnotations()
        }
        if (e.key === 'F11') {
          e.preventDefault()
          toggleFullscreen()
        }
        return
      }

      // Navigation in display order
      if (e.key === 'q' || e.key === 'Q') {
        const schema = getKeypointSchema()
        if (schema.length > 0) {
          const newIndex = navigateKeypointInDisplayOrder(currentKeypoint, schema, 'prev')
          setCurrentKeypoint(newIndex)
          setShouldAutoScroll(true) // Enable auto-scroll for keyboard navigation
        }
      } else if (e.key === 'w' || e.key === 'W') {
        const schema = getKeypointSchema()
        if (schema.length > 0) {
          const newIndex = navigateKeypointInDisplayOrder(currentKeypoint, schema, 'next')
          setCurrentKeypoint(newIndex)
          setShouldAutoScroll(true) // Enable auto-scroll for keyboard navigation
        }
      }
      // Save
      else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        saveAnnotations()
      }
      // A/S keys for image navigation
      else if ((e.key === 'a' || e.key === 'A') && currentImageIndex > 0) {
        navigateImage(currentImageIndex - 1, true) // Pass true for keyboard navigation
      } else if ((e.key === 's' || e.key === 'S') && currentImageIndex < images.length - 1) {
        navigateImage(currentImageIndex + 1, true) // Pass true for keyboard navigation
      }
      // Delete annotation
      else if (e.key === 'Delete' && selectedAnnotationId) {
        handleAnnotationDelete(selectedAnnotationId)
      }
      // Fullscreen toggle
      else if (e.key === 'F11') {
        e.preventDefault()
        toggleFullscreen()
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [
    currentKeypoint,
    annotations,
    project,
    currentImageIndex,
    images.length,
    selectedAnnotationId,
    saveAnnotations,
    toggleFullscreen,
  ])

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-4">Loading annotation tool...</p>
      </div>
    )
  }

  if (!project || images.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">No images to annotate</p>
        <button className="btn-primary" onClick={() => navigate('/projects')}>
          Back to Projects
        </button>
      </div>
    )
  }

  const currentImage = images[currentImageIndex]
  const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId) || null
  const getAnnotationCount = () => {
    return Array.from(imageAnnotations.values()).length
  }

  return (
    <div
      className={`${isFullscreen ? 'fixed inset-0 z-50' : 'h-screen'} flex flex-col bg-gray-100`}
    >
      {/* Header */}
      <div
        className={`bg-white border-b border-gray-200 px-4 ${isFullscreen ? 'py-2' : 'pt-4 pb-3'}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {!isFullscreen ? (
              <>
                <button
                  onClick={() => navigate('/projects')}
                  className="mr-4 text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-xl font-semibold">{project.name}</h1>
                  <p className="text-sm text-gray-600">
                    Image {currentImageIndex + 1} of {images.length} - {currentImage.filename}
                  </p>
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/projects')}
                  className="mr-3 text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                </button>
                <div className="flex items-center">
                  <h1 className="text-base font-semibold">{project.name}</h1>
                  <span className="ml-2 text-sm text-gray-600">
                    - Image {currentImageIndex + 1}/{images.length} - {currentImage.filename}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className={`flex items-center ${isFullscreen ? 'gap-3' : 'gap-4'}`}>
            {/* Project Status */}
            {userProjectStatus && (
              <div className={`flex items-center gap-2 ${isFullscreen ? 'text-xs' : 'text-sm'}`}>
                <span className="text-gray-600">Status:</span>
                <StatusBadge
                  status={userProjectStatus.status}
                  className={isFullscreen ? 'text-xs' : ''}
                />
              </div>
            )}

            <div className={`flex items-center gap-2 ${isFullscreen ? 'text-xs' : 'text-sm'}`}>
              <DocumentCheckIcon className={isFullscreen ? 'w-4 h-4' : 'w-5 h-5'} />
              <span className="text-gray-600">
                {getAnnotationCount()} / {images.length} annotated
              </span>
            </div>

            {hasUnsavedChanges && (
              <div
                className={`flex items-center gap-2 text-amber-600 ${isFullscreen ? 'text-xs' : 'text-sm'}`}
              >
                <ExclamationTriangleIcon className={isFullscreen ? 'w-4 h-4' : 'w-5 h-5'} />
                <span>Unsaved changes</span>
              </div>
            )}

            {/* Messages */}
            {projectId && (
              <MessageNotification
                projectId={projectId}
                className={isFullscreen ? 'scale-90' : ''}
              />
            )}

            <button
              onClick={saveAnnotations}
              disabled={saving}
              className={isFullscreen ? 'btn-primary text-xs py-2 px-3' : 'btn-primary'}
            >
              {saving ? 'Saving...' : 'Save (Ctrl+S)'}
            </button>

            {/* QA Status Button */}
            {userProjectStatus && (
              <>
                {userProjectStatus.status === ProjectStatus.DRAFT ||
                userProjectStatus.status === ProjectStatus.NEEDS_REVISION ? (
                  <button
                    onClick={handleSubmitForQA}
                    className={`flex items-center gap-2 rounded-lg transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200 ${
                      isFullscreen ? 'text-xs py-2 px-3' : 'px-4 py-2'
                    }`}
                  >
                    <CheckCircleIcon className={isFullscreen ? 'w-4 h-4' : 'w-5 h-5'} />
                    <span>Submit for QA</span>
                  </button>
                ) : userProjectStatus.status === ProjectStatus.QA_PENDING ? (
                  <button
                    onClick={handleWithdrawFromQA}
                    className={`flex items-center gap-2 rounded-lg transition-colors bg-orange-100 text-orange-700 hover:bg-orange-200 ${
                      isFullscreen ? 'text-xs py-2 px-3' : 'px-4 py-2'
                    }`}
                    title="Click to withdraw from QA review"
                  >
                    <ArrowUturnLeftIcon className={isFullscreen ? 'w-4 h-4' : 'w-5 h-5'} />
                    <span>Withdraw from QA</span>
                  </button>
                ) : userProjectStatus.status === ProjectStatus.QA_APPROVED ? (
                  <div
                    className={`flex items-center gap-2 rounded-lg bg-green-50 text-green-600 ${
                      isFullscreen ? 'text-xs py-2 px-3' : 'px-4 py-2'
                    }`}
                  >
                    <CheckCircleIconSolid className={isFullscreen ? 'w-4 h-4' : 'w-5 h-5'} />
                    <span>QA Approved</span>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar - Keypoint selector */}
        <div className="w-56 bg-white border-r border-gray-200 p-4 flex flex-col overflow-hidden">
          {/* Current editing status */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 h-24">
            {selectedAnnotation ? (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-600">
                    {currentImage.meta_data?.source === 'video_extraction' ? 'Frame' : 'Image'}:
                  </span>
                  <span className="text-sm font-semibold text-gray-700">
                    {currentImage.frame_number || currentImageIndex + 1} / {images.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-600">Annotation:</span>
                  <span className="text-sm font-semibold text-blue-700">
                    {annotations.findIndex(a => a.id === selectedAnnotationId) + 1} /{' '}
                    {annotations.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-600">Keypoint:</span>
                  <span className="text-sm font-semibold text-red-700">
                    {project.keypoint_schema[currentKeypoint] || `Point ${currentKeypoint + 1}`}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-gray-600">Select or create a bounding box first</p>
              </div>
            )}
          </div>

          {/* Additional Controls */}
          <div className="mb-4">
            <div className="space-y-4">
              {/* Keypoint Radius */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Keypoint Radius
                </label>
                <input
                  type="number"
                  value={keypointRadius}
                  onChange={e => {
                    const newRadius = Math.max(1, Math.min(20, parseInt(e.target.value) || 5))
                    setKeypointRadius(newRadius)
                    setCurrentViewSettings(prev => ({
                      scale: prev?.scale,
                      offset: prev?.offset,
                      keypointRadius: newRadius,
                      rectangleWidth: prev?.rectangleWidth ?? rectangleWidth,
                    }))
                    // Don't trigger unsaved changes for view settings
                  }}
                  min="1"
                  max="20"
                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Rectangle Width */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rectangle Width
                </label>
                <input
                  type="number"
                  value={rectangleWidth}
                  onChange={e => {
                    const newWidth = Math.max(1, Math.min(20, parseInt(e.target.value) || 3))
                    setRectangleWidth(newWidth)
                    setCurrentViewSettings(prev => ({
                      scale: prev?.scale,
                      offset: prev?.offset,
                      keypointRadius: prev?.keypointRadius ?? keypointRadius,
                      rectangleWidth: newWidth,
                    }))
                    // Don't trigger unsaved changes for view settings
                  }}
                  min="1"
                  max="20"
                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 my-4"></div>

            {/* Poor Quality Toggle for current image */}
            <div>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm font-medium text-gray-700">Poor Quality</span>
                <input
                  type="checkbox"
                  checked={isPoorQuality}
                  onChange={e => {
                    if (!checkEditPermission()) {
                      e.target.checked = isPoorQuality // Revert the checkbox
                      return
                    }
                    setIsPoorQuality(e.target.checked)
                    setHasUnsavedChanges(true)
                  }}
                  className="ml-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </label>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 mt-4"></div>
          </div>

          <KeypointSelector
            keypointNames={project.keypoint_schema}
            currentKeypoint={currentKeypoint}
            selectedAnnotation={selectedAnnotation}
            onKeypointSelect={setCurrentKeypoint}
            onClearKeypoint={handleClearKeypoint}
            onClearAll={handleClearAll}
            scrollRef={keypointScrollRef}
          />
        </div>

        {/* Canvas */}
        <div className="flex-1 min-w-0 flex">
          <AnnotationCanvas
            image={currentImage}
            project={project}
            annotations={annotations}
            currentKeypoint={currentKeypoint}
            selectedAnnotationId={selectedAnnotationId}
            onAnnotationSelect={handleAnnotationSelect}
            onAnnotationAdd={handleAnnotationAdd}
            onAnnotationUpdate={handleAnnotationUpdate}
            onAnnotationDelete={handleAnnotationDelete}
            onKeypointUpdate={handleKeypointUpdate}
            viewSettings={currentViewSettings || undefined}
            onViewSettingsChange={handleViewSettingsChange}
            keypointRadius={keypointRadius}
            rectangleWidth={rectangleWidth}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
            shouldAutoFit={shouldAutoFit}
            onAutoFitComplete={() => setShouldAutoFit(false)}
          />
        </div>

        {/* Right sidebar - Image list */}
        <div className="w-56 bg-white border-l border-gray-200 p-4 flex flex-col overflow-hidden">
          {/* Navigation controls */}
          <div className="mb-4">
            <div className="flex justify-between mb-2">
              <button
                onClick={() => navigateImage(currentImageIndex - 1)}
                disabled={currentImageIndex === 0}
                className="btn-secondary flex items-center text-sm py-1 px-3"
              >
                <ChevronLeftIcon className="w-4 h-4 mr-1" />
                Previous
              </button>

              <button
                onClick={() => navigateImage(currentImageIndex + 1)}
                disabled={currentImageIndex === images.length - 1}
                className="btn-secondary flex items-center text-sm py-1 px-3"
              >
                Next
                <ChevronRightIcon className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>

          <div ref={frameScrollRef} className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
            <div className="space-y-2" id="frame-list">
              {images.map((img, index) => {
                const hasAnnotation = imageAnnotations.has(img.id)
                const isCurrentImage = index === currentImageIndex

                return (
                  <button
                    key={img.id}
                    id={`frame-${index}`}
                    onClick={() => navigateImage(index)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      isCurrentImage
                        ? 'bg-blue-50 border-2 border-blue-500'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {img.meta_data?.source === 'video_extraction'
                            ? `Frame ${img.frame_number || index + 1}`
                            : `Image ${index + 1}`}
                        </p>
                      </div>
                      {hasAnnotation ? (
                        <CheckCircleIconSolid className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex-shrink-0 ml-2" />
                      )}
                    </div>
                    {isCurrentImage && hasUnsavedChanges && (
                      <div className="mt-1 text-xs text-amber-600">Unsaved changes</div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
